import type React from "react";
import { useMemo } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { DropdownButton } from "@similarweb/ui-components";

import { getCountries } from "app/components/filters-bar/utils";
import { translate } from "app/filters/i18nFilter";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import {
    StyledCountryIcon,
    StyledDDContainer,
} from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/components/CountriesDropdown/styles";
import { WithTitleContainer } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/components/WithTitleContainer/WithTitleContainer";
import { StyledBaseDropdownButtonWrap } from "app/pages/sales-intelligence/sub-modules/advanced-search/components/common-styles";
import CountriesDropdownComponent from "app/pages/sales-intelligence/sub-modules/advanced-search/components/shared/CountriesDropdown/CountriesDropdown";
import type { ICountryObject } from "app/services/CountryService";

import { HookBanner } from "./HookBanner";

const DD_ID = "si-agent-knowledge-base-countries-dd";
const DD_POPUP_HEIGHT = 48 * 4;

type Props = {
    value: number;
    onChange: (value: number) => void;
    dataAutomation?: string;
};

export const CountriesDropdown: React.FC<Props> = ({ value, onChange, dataAutomation }) => {
    const allCountries: ICountryObject[] = useMemo(
        () =>
            getCountries().map(
                ({ key, ...rest }) => ({ ...rest, isLastPermitted: false }) as ICountryObject,
            ),
        [],
    );

    const hasNotPermittedCountries = allCountries.some((country) => country.permitted === false);
    const selectedCountry = allCountries.find((country) => country.id === value)!;

    const handleSelect = (country: ICountryObject | null) => {
        if (country) {
            AIAgentTrackingService.kb.trackCountrySelect(country.text);
            onChange(country.id);
        }
    };

    const renderDDButton = () => {
        return (
            <StyledBaseDropdownButtonWrap
                key="countries-dropdown-button"
                buttonTextColor={colorsPalettes.carbon[400]}
            >
                <DropdownButton>
                    <StyledCountryIcon
                        countryCode={selectedCountry?.id}
                        size="sm"
                    />
                    {selectedCountry?.text}
                </DropdownButton>
            </StyledBaseDropdownButtonWrap>
        );
    };

    return (
        <WithTitleContainer
            title={translate("si.agent_knowledge_base.modal.country.title")}
            dataAutomation={dataAutomation}
        >
            <StyledDDContainer
                id={DD_ID}
                withPadding={hasNotPermittedCountries}
            >
                <CountriesDropdownComponent
                    appendTo={`#${DD_ID}`}
                    onSelect={handleSelect}
                    selectedItems={[selectedCountry?.id]}
                    items={allCountries}
                    placeholder={translate("si.pages.ai_agent.form.countries.placeholder")}
                    virtualize={false}
                    dropdownPopupHeight={DD_POPUP_HEIGHT}
                    dropdownPopupPlacement="ontop-left"
                    renderButton={renderDDButton}
                    headerComponent={hasNotPermittedCountries ? <HookBanner /> : null}
                />
            </StyledDDContainer>
        </WithTitleContainer>
    );
};
